import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import {StringUtil} from "../../../util/string.util";
import {MatDialog} from "@angular/material/dialog";
import {NgModel} from "@angular/forms";
import {ValidationService} from "../../../util/service/validation/validation-service";
import {Subscription} from "rxjs";
import {SelectUserOptions} from "../select-user/options/select-user.options";
import {SelectUserComponent} from "../select-user/select-user.component";

@Component({
  selector: 'app-frontend-input',
  standalone: false,
  templateUrl: './frontend-input.component.html',
  styleUrls: ['./frontend-input.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class FrontendInputComponent implements OnInit, OnDestroy, OnChanges {
  hide = true;
  @Input()
  value?: any;
  @Input()
  title?: string;
  @Input()
  placeHolder: string = '';
  @Input()
  required: boolean = false;
  @Input()
  disabled: boolean = false;
  @Input()
  type?: string;
  @Input()
  thousandSeparator: boolean = false;
  @Input()
  englishNumberOnly: boolean = false;
  @Output()
  valueChange: EventEmitter<any> = new EventEmitter();
  @Input()
  lookup?: string;
  @Output()
  lookupSelected: EventEmitter<any> = new EventEmitter();
  @ViewChild('inputControl', {static: true}) inputControl?: NgModel;
  internalValue?: any;
  private subscription?: Subscription;

  constructor(private matDialog: MatDialog, private validationService: ValidationService) {
  }

  ngOnInit() {
    this.subscription = this.validationService.validate$.subscribe(() => {
      this.checkValidity();
    });
    this.syncInternalFromExternal();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['value'] && !changes['value'].firstChange) {
      this.syncInternalFromExternal();
    }
  }

  checkValidity() {
    const isInvalid = this.required && (this.value === undefined || this.value === null || this.value === '' || (typeof this.value === 'number' && isNaN(this.value)));
    const isValid = !isInvalid;
    this.inputControl!.control.markAsTouched();
    this.inputControl!.control.setErrors(isValid ? null : {required: true});
    this.validationService.updateValidationStatus(isValid);
  }

  onTouched() {
    this.inputControl!.control.markAsTouched();
  }

  onKeyPress(event: KeyboardEvent) {
    if (this.type === 'number') {
      const char = event.key;
      // Allow Persian digits (۰-۹), Arabic digits (٠-٩), English digits (0-9), decimal point, comma, and control keys
      const allowedChars = /[0-9۰-۹٠-٩.,\s]/;
      const controlKeys = ['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
      if (!allowedChars.test(char) && !controlKeys.includes(char) && !event.ctrlKey && !event.metaKey) {
        event.preventDefault();
      }
    }
  }

  onChange() {
    if (this.thousandSeparator) {
      const raw = String(this.internalValue ?? '');
      const normalized = (StringUtil.reformatString(raw) ?? '').replace(/[,\s]/g, '');
      const parsed = parseFloat(normalized);
      const safeNumber = isNaN(parsed) ? undefined : Math.round(parsed);
      if (safeNumber !== undefined) {
        this.internalValue = StringUtil.numberAsCurrency(safeNumber);
      } else {
        this.internalValue = '';
      }
      this.valueChange.emit(safeNumber);
    } else if (this.type === 'number') {
      // Convert Persian numbers to English and parse
      const persianToEnglish = String(this.internalValue ?? '')
        .replace(/[۰-۹]/g, (match) => String.fromCharCode(match.charCodeAt(0) - '۰'.charCodeAt(0) + '0'.charCodeAt(0)))
        .replace(/[٠-٩]/g, (match) => String.fromCharCode(match.charCodeAt(0) - '٠'.charCodeAt(0) + '0'.charCodeAt(0)));
      const parsed = parseFloat(persianToEnglish);
      this.valueChange.emit(isNaN(parsed) ? undefined : parsed);
    } else {
      let temp = StringUtil.reformatString(this.internalValue);
      this.valueChange.emit(temp);
    }
  }

  ngOnDestroy() {
    this.subscription!.unsubscribe();
  }

  onLookupClicked() {
    switch (this.lookup) {
      case 'CLIENT':
      case 'STAFF':
        let selectUserOptions = new SelectUserOptions();
        selectUserOptions.userType = this.lookup;
        selectUserOptions.onUserSelected = (user: any) => {
          this.value = user.FirstName + user.LastName;
          this.lookupSelected.emit(user);
          this.valueChange.emit(user.UserId);
        };
        let selectUserComponent = this.matDialog.open(SelectUserComponent, {
          height: 'auto',
          width: '100%'
        });
        selectUserComponent.componentInstance.selectUserOptions = selectUserOptions;
        break;
    }
  }

  getRawValue(): number | undefined {
    if (this.thousandSeparator) {
      const raw = String(this.internalValue ?? '');
      const normalized = (StringUtil.reformatString(raw) ?? '').replace(/[,\s]/g, '');
      const parsed = parseFloat(normalized);
      return isNaN(parsed) ? undefined : Math.round(parsed);
    } else if (this.type === 'number') {
      // Convert Persian numbers to English and parse
      const persianToEnglish = String(this.internalValue ?? '')
        .replace(/[۰-۹]/g, (match) => String.fromCharCode(match.charCodeAt(0) - '۰'.charCodeAt(0) + '0'.charCodeAt(0)))
        .replace(/[٠-٩]/g, (match) => String.fromCharCode(match.charCodeAt(0) - '٠'.charCodeAt(0) + '0'.charCodeAt(0)));
      const parsed = parseFloat(persianToEnglish);
      return isNaN(parsed) ? undefined : parsed;
    }
    return this.value;
  }

  private syncInternalFromExternal() {
    if (this.thousandSeparator) {
      const parsed = parseFloat(this.value);
      if (!isNaN(parsed)) {
        this.internalValue = StringUtil.numberAsCurrency(Math.round(parsed));
      } else {
        this.internalValue = '';
      }
    } else if (this.type === 'number') {
      // For number inputs, display the value as is (can be Persian or English)
      this.internalValue = this.value;
    } else {
      this.internalValue = this.value;
    }
  }
}
